import React, { useCallback, useEffect, useState } from 'react'

import { Input, InputWrapper, Select } from '@amzn/stencil-react-components/form'
import { Col, Row, Spacer } from '@amzn/stencil-react-components/layout'
import { useBreakpoints } from '@amzn/stencil-react-components/responsive'
import { Text } from '@amzn/stencil-react-components/text'

import { SmartExpanderContent } from 'src/components/SmartExpander'
import { isDefaultLocale } from 'src/contexts/ModuleLocaleContext'
import { useActivityEntity, useValidationErrorEntity } from 'src/hooks/useEntity'
import { IScreenEntity } from 'src/models/dto/activities/IScreenDTO'
import { ItemEditorNumberInput } from 'src/pages/module-builder/item-editors/ItemEditorInputs'
import { ItemEditorValidationErrors } from 'src/pages/module-builder/item-editors/ItemEditorValidationErrors'
import { activityDisplayNameMap } from 'src/pages/module-builder/page-editor/ActivityDropDown'
import { PageEditorProps } from 'src/pages/module-builder/page-editor/PageEditor'
import { PageHeader } from 'src/pages/module-builder/page-editor/PageHeader'
import { IScreenHandler } from 'src/services/EntityServices/ActivityUpdateHandlers/IScreenHandler'

const COMPETENCY_TYPE_OPTIONS = [
    { name: 'Core Competency', value: 'CORE' },
    {
        name: 'Leadership Principle',
        value: 'LEADERSHIP',
    },
    { name: 'Requisition', value: 'REQUISITION' },
    { name: 'Technical/Functional', value: 'TECHNICAL' },
]

// Helper to find display name from backend value
const getCompetencyTypeName = (value: string | null) => {
    if (!value) return undefined
    return COMPETENCY_TYPE_OPTIONS.find((opt) => opt.value === value)?.name
}

// Helper to find backend value from display name
const getCompetencyTypeValue = (name: string | undefined) => {
    if (!name) return null
    return COMPETENCY_TYPE_OPTIONS.find((opt) => opt.name === name)?.value ?? null
}

const MAX_NUMBER_OF_COMPETENCIES = 6

const NUMBER_OF_COMPETENCIES_OPTIONS = Array.from(
    { length: MAX_NUMBER_OF_COMPETENCIES },
    (_, i) => ({
        name: (i + 1).toString(),
        value: i + 1,
    })
)

export const IScreenEditor = ({
    pageIndex,
    locale,
    moduleEntityId,
    workflowEntityId,
    isLastPage,
    showValidationError,
    editDisabled,
    isExpanded,
    onToggleExpand,
}: PageEditorProps) => {
    const { matches } = useBreakpoints()

    const { entity } = useActivityEntity<IScreenEntity>({
        workflowEntityId,
        moduleEntityId,
    })

    const { entity: validationErrorEntity } = useValidationErrorEntity(workflowEntityId)

    const [showError, setShowError] = useState(showValidationError)

    useEffect(() => {
        setShowError(showValidationError ?? false)
    }, [showValidationError])

    const handleNumberOfCompetenciesChange = useCallback(
        (value: number | null) => {
            IScreenHandler.updateNumberOfCompetencies(entity.id, value)
        },
        [entity.id]
    )

    const handleCompetencyTypeChange = useCallback(
        (competencyId: string, value: string | null) => {
            IScreenHandler.updateCompetencyType(entity.id, competencyId, value)
        },
        [entity.id]
    )

    const handleCompetencyTimeLimitChange = useCallback(
        (competencyId: string, value: number | null) => {
            IScreenHandler.updateCompetencyTimeLimit(entity.id, competencyId, value)
        },
        [entity.id]
    )

    return (
        <Col gridGap='S300' dataTestId={`page-editor-${pageIndex}`}>
            <Spacer height='S100' />
            <PageHeader
                isLastPage={isLastPage}
                moduleEntityId={moduleEntityId}
                workflowEntityId={workflowEntityId}
                isExpanded={isExpanded}
                pageIndex={pageIndex}
                onclick={onToggleExpand}
                activityName={activityDisplayNameMap.get(entity.type)}
                showValidationError={showError}
                onErrorCheck={setShowError}
                editDisabled={editDisabled}
            />
            <SmartExpanderContent isExpanded={isExpanded}>
                {() => (
                    <>
                        <ItemEditorValidationErrors
                            itemId={workflowEntityId}
                            showValidationError={showError}
                        />
                        <Row gridGap='S300' alignItems={'flex-start'} flexWrap={'wrap'}>
                            <Col flex={`3 0 ${matches.s ? '100%' : '250px'}`}>
                                <ItemEditorNumberInput
                                    dataTestId={'iscreen-time-limit'}
                                    inputId={'iscreen-time-limit'}
                                    itemId={entity.id}
                                    labelText='Interview validity duration days'
                                    value={entity.interviewValidityDurationDays}
                                    disabled={!isDefaultLocale(locale) || editDisabled}
                                    placeholder='Enter number value in days'
                                    setValue={(nextValue: number | null) => {
                                        IScreenHandler.updateInterviewValidityDurationDays(
                                            entity.id,
                                            nextValue
                                        )
                                    }}
                                    validationErrorMessage={(
                                        validationErrorEntity.validationErrors
                                            .interviewValidityDurationDays ?? []
                                    ).join(', ')}
                                    showError={showError}
                                />
                            </Col>
                        </Row>
                        <Spacer height='S200' />
                        <Row gridGap='S300' alignItems={'flex-start'} flexWrap={'wrap'}>
                            <Col flex={`3 0 ${matches.s ? '100%' : '250px'}`}>
                                <InputWrapper
                                    id='iscreen-number-of-competencies'
                                    labelText='Number of competencies'
                                >
                                    {(inputProps) => (
                                        <Select
                                            dataTestId='iscreen-number-of-competencies'
                                            {...inputProps}
                                            disabled={!isDefaultLocale(locale) || editDisabled}
                                            options={NUMBER_OF_COMPETENCIES_OPTIONS.map(
                                                (opt) => opt.value
                                            )}
                                            value={entity.numberOfCompetencies ?? undefined}
                                            onChange={(value: number | undefined) => {
                                                handleNumberOfCompetenciesChange(
                                                    value !== undefined ? value : null
                                                )
                                            }}
                                        />
                                    )}
                                </InputWrapper>
                            </Col>
                        </Row>
                        <Spacer height='S200' />
                        {entity.iscreenCompetencies.length > 0 && (
                            <>
                                <Spacer height='S200' />
                                <Text fontSize='T200' fontWeight='bold'>
                                    Competency types
                                </Text>
                                <Spacer height='S300' />
                                {entity.iscreenCompetencies.map((competency, index) => (
                                    <React.Fragment key={competency.id}>
                                        <Row
                                            gridGap='S300'
                                            alignItems={'flex-end'}
                                            flexWrap={'wrap'}
                                        >
                                            <Col flex={`1 0 ${matches.s ? '100%' : '250px'}`}>
                                                <InputWrapper
                                                    id={`competency-type-${index + 1}`}
                                                    labelText={`Competency type ${index + 1}`}
                                                >
                                                    {(inputProps) => (
                                                        <Select
                                                            dataTestId={`competency-type-${
                                                                index + 1
                                                            }`}
                                                            {...inputProps}
                                                            disabled={
                                                                !isDefaultLocale(locale) ||
                                                                editDisabled
                                                            }
                                                            options={COMPETENCY_TYPE_OPTIONS.map(
                                                                (opt) => opt.name
                                                            )}
                                                            value={getCompetencyTypeName(
                                                                competency.competencyType
                                                            )}
                                                            onChange={(
                                                                name: string | undefined
                                                            ) => {
                                                                handleCompetencyTypeChange(
                                                                    competency.id,
                                                                    getCompetencyTypeValue(name)
                                                                )
                                                            }}
                                                        />
                                                    )}
                                                </InputWrapper>
                                            </Col>
                                            <Col flex={`1 0 ${matches.s ? '100%' : '250px'}`}>
                                                <InputWrapper
                                                    id={`competency-${index + 1}-time-limit`}
                                                    labelText={`Competency ${index + 1} time limit`}
                                                >
                                                    {(inputProps) => (
                                                        <Input
                                                            dataTestId={`competency-${
                                                                index + 1
                                                            }-time-limit`}
                                                            {...inputProps}
                                                            type='number'
                                                            min='1'
                                                            disabled={
                                                                !isDefaultLocale(locale) ||
                                                                editDisabled
                                                            }
                                                            value={
                                                                competency.iscreenCompetencyTimeLimitSeconds?.toString() ??
                                                                ''
                                                            }
                                                            placeholder='360'
                                                            onChange={(e) => {
                                                                const value = e.target.value
                                                                const numValue = Number(value)
                                                                handleCompetencyTimeLimitChange(
                                                                    competency.id,
                                                                    value && numValue >= 1
                                                                        ? numValue
                                                                        : null
                                                                )
                                                            }}
                                                        />
                                                    )}
                                                </InputWrapper>
                                            </Col>
                                        </Row>
                                        {index < entity.iscreenCompetencies.length - 1 && (
                                            <Spacer height='S200' />
                                        )}
                                    </React.Fragment>
                                ))}
                            </>
                        )}
                        <Spacer height='S400' />
                    </>
                )}
            </SmartExpanderContent>
        </Col>
    )
}
